import React from 'react';
import {
  Box,
  Typography,
} from '@mui/material';
import { APP_CONFIG } from '../../../../config/app.config';

interface WarehouseDataPreviewTableProps {
  headers: string[];
  rows: string[][];
  columnMapping?: {
    ean?: string;
    quantity?: string;
  };
  onDragStart: (e: React.DragEvent, columnName: string) => void;
  onDragEnd: () => void;
  onColumnClick: (columnName: string) => void;
}

const WarehouseDataPreviewTable: React.FC<WarehouseDataPreviewTableProps> = ({
  headers,
  rows,
  columnMapping,
  onDragStart,
  onDragEnd,
  onColumnClick,
}) => {
  return (
    <Box sx={{ display: 'flex', height: '100%', p: 3, overflow: 'auto' }}>
      <Box sx={{ display: 'flex', height: '100%', overflow: 'clip', borderRadius: 2, border: 1, borderColor: APP_CONFIG.ui.theme.colors.borderHover }}>
        {headers.map((header, colIndex) => {
          const isEanColumn = header === columnMapping?.ean;
          const isQuantityColumn = header === columnMapping?.quantity;

          // Skip rendering mapped columns in the table view only for file data
          // For Monta data, show all columns so users can see what's available to map
          if (isEanColumn || isQuantityColumn) {
            return null;
          }

          return (
            <Box
              key={colIndex}
              draggable
              onDragStart={(e) => onDragStart(e, header)}
              onDragEnd={onDragEnd}
              onClick={() => onColumnClick(header)}
              sx={{
                maxWidth: '200px',
                border: '1px solid',
                borderColor: (isEanColumn || isQuantityColumn) ? 'secondary.main' : APP_CONFIG.ui.theme.colors.border,
                backgroundColor: (isEanColumn || isQuantityColumn) ? 'secondary.50' : 'white',
                cursor: (isEanColumn || isQuantityColumn) ? 'grab' : 'grab',
                '&:hover': {
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                  backgroundColor: (isEanColumn || isQuantityColumn) ? 'secondary.100' : 'grey.50',
                },
                display: 'flex',
                flexDirection: 'column',
                opacity: (isEanColumn || isQuantityColumn) ? 0.8 : 1,
              }}
            >
              {/* Column Header */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: '1px solid',
                  borderColor: APP_CONFIG.ui.theme.colors.borderHover,
                  backgroundColor: APP_CONFIG.ui.theme.colors.background,
                  textAlign: 'left',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    overflow: 'hidden',
                    fontWeight: 'bold',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: 'text.primary',
                  }}
                >
                  {header || `Column ${colIndex + 1}`}
                </Typography>
              </Box>

              {/* Column Data */}
              <Box sx={{ flex: 1, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
                {Array.from({ length: 10 }).map((_, rowIndex) => {
                  const cellValue = rows[rowIndex] ? rows[rowIndex][colIndex] : '';

                  return (
                    <Box
                      key={rowIndex}
                      sx={{
                        p: 2,
                        flexGrow: 1,
                        borderBottom: '1px solid',
                        borderColor: APP_CONFIG.ui.theme.colors.border,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          width: '100%',
                          textAlign: 'left',
                        }}
                      >
                        {cellValue}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default WarehouseDataPreviewTable; 